// src/pages/contracts/ExternalContractAccess.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Container, Chip, IconButton, Divider, 
  Tooltip, Paper, Stack, Fade, Zoom, LinearProgress, Slide,
  InputAdornment, Stepper, Step, StepLabel, Avatar, Grid,
} from '@mui/material';
import {
  CheckCircle as SignedIcon,
  Description as DocumentIcon,
  Error as ErrorIcon,
  Gavel as ContractIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Article as ArticleIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon,
  AccountCircle as AccountCircleIcon,
  Create as CreateIcon,
  ArrowForward as ArrowForwardIcon,
  Clear as ClearIcon,
  Gesture as GestureIcon,
} from '@mui/icons-material';
import PageTitle from '../../Components/PageTitle';
import client from '../../API';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SignatureCanvas from 'react-signature-canvas';

// Enhanced modern theme
const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1e40af',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
  },
});

const ExternalContractAccess = () => {
  const { id: signatureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [error, setError] = useState(null);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [completed, setCompleted] = useState(false);
  const [accessToken, setAccessToken] = useState(null);

  // State for editing signer info
  const [showEditInfoDialog, setShowEditInfoDialog] = useState(false);
  const [editedSignerInfo, setEditedSignerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // State for signature confirmation
  const [showSignConfirmDialog, setShowSignConfirmDialog] = useState(false);
  const [signatureStep, setSignatureStep] = useState(0);

  // NEW: Signature pad state
  const [signatureData, setSignatureData] = useState(null);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const signaturePadRef = useRef(null);

  const completedDocRef = useRef(null);

  // Extract token from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      setAccessToken(token);
    } else {
      setError('Access token is required');
      setLoading(false);
    }
  }, [location.search]);

  const load = useCallback(async () => {
    if (!signatureId || !accessToken) {
      setError('Invalid contract access link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await client.contracts.getExternalContract(signatureId, accessToken);
      
      setContractData(data);
      
      // Initialize edited signer info with current values
      if (data.signer_info) {
        setEditedSignerInfo({
          name: data.signer_info.name || '',
          email: data.signer_info.email || '',
          phone: data.signer_info.phone || ''
        });
      }
    } catch (e) {
      console.error('Failed to load external contract:', e);
      setError(e.response?.data?.error || e.message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [signatureId, accessToken]);

  useEffect(() => {
    if (accessToken) {
      load();
    }
  }, [load, accessToken]);

  // Handle info editing
  const handleEditInfo = () => {
    setShowEditInfoDialog(true);
  };

  const handleSaveEditedInfo = () => {
    setShowEditInfoDialog(false);
  };

  // NEW: Signature pad handlers
  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setSignatureData(null);
      setSignatureEmpty(true);
    }
  };

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataUrl = signaturePadRef.current.toDataURL('image/png');
      setSignatureData(dataUrl);
      setSignatureEmpty(false);
    } else {
      setSignatureEmpty(true);
    }
  };

  // Enhanced agree/sign flow with confirmation
  const handleAgree = async () => {
    if (!contractData?.is_simple) return handleInitiateSign();
    
    setSignatureStep(0);
    setShowSignConfirmDialog(true);
  };

  const handleInitiateSign = () => {
    setSignatureStep(0);
    setShowSignConfirmDialog(true);
    // Reset signature when opening dialog
    setSignatureData(null);
    setSignatureEmpty(true);
  };

  const handleConfirmAndSign = async () => {
    // For complex contracts, validate signature is drawn
    if (!contractData.is_simple && signatureEmpty) {
      setError('Please draw your signature before proceeding');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const signerInfo = {
        name: editedSignerInfo.name,
        email: editedSignerInfo.email,
        phone: editedSignerInfo.phone
      };

      let result;
      if (contractData.is_simple) {
        result = await client.contracts.agreeExternalContract(
          signatureId, 
          accessToken,
          signerInfo
        );
      } else {
        // Include signature data for complex contracts
        result = await client.contracts.signExternalContract(
          signatureId, 
          accessToken,
          {
            ...signerInfo,
            signature_image: signatureData // NEW: Send signature image
          }
        );
      }
      
      setCompleted(true);
      setContractData(prev => ({
        ...prev,
        signature: result.external_signature || result.signature,
        signer_info: signerInfo,
        signature_image: signatureData // Store for display
      }));
      
      setShowSignConfirmDialog(false);
      
      await load();
    } catch (e) {
      console.error('Failed to sign contract:', e);
      setError(e.response?.data?.error || e.message || 'Failed to sign contract');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const result = await client.contracts.declineExternalContract(
        signatureId, 
        accessToken, 
        declineReason
      );
      
      setCompleted(true);
      setContractData(prev => ({
        ...prev,
        signature: result.external_signature || result.signature
      }));
      setShowDeclineDialog(false);
      
      await load();
    } catch (e) {
      console.error('Failed to decline contract:', e);
      setError(e.response?.data?.error || e.message || 'Failed to decline contract');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    const canClose = window.opener !== null || window.history.length <= 1;
    if (canClose) {
      window.close();
    } else {
      navigate('/');
    }
  };

  // Handle account claim
  const handleClaimAccount = () => {
    const params = new URLSearchParams({
      email: editedSignerInfo.email || contractData.signer_info?.email || '',
      tab: 'creator',
      name: editedSignerInfo.name || contractData.signer_info?.name || '',
      phone: editedSignerInfo.phone || contractData.signer_info?.phone || ''
    });
    
    navigate(`/onboard?${params.toString()}`);
  };

  const downloadPdf = async () => {
    if (!completedDocRef.current) return;
    const element = completedDocRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`external_contract_${signatureId || 'completed'}.pdf`);
  };

  if (loading) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Loading Contract - Blitz" />
        <Box sx={{ 
          minHeight: '100vh', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: 4, textAlign: 'center' }}>
            <CircularProgress size={56} thickness={4} />
            <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
              Loading contract...
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Contract Error - Blitz" />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Fade in>
            <Box>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 4, 
                  borderRadius: 3,
                  '& .MuiAlert-icon': { fontSize: 32 }
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Contract Access Error
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
              <Stack direction="row" spacing={2}>
                <Button 
                  variant="outlined" 
                  onClick={handleClose} 
                  startIcon={<BackIcon />}
                  size="large"
                >
                  Close
                </Button>
                <Button 
                  variant="contained" 
                  onClick={load} 
                  startIcon={<RefreshIcon />}
                  size="large"
                >
                  Try Again
                </Button>
              </Stack>
            </Box>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  if (!contractData) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Contract Not Found - Blitz" />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Fade in>
            <Box>
              <Alert severity="warning" sx={{ borderRadius: 3, mb: 3 }}>
                Contract not found or access denied.
              </Alert>
              <Button 
                variant="outlined" 
                onClick={handleClose} 
                startIcon={<BackIcon />}
                size="large"
              >
                Close
              </Button>
            </Box>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  const { signature, processed_contract_body, is_simple, signer_info, template } = contractData;

  // COMPLETED VIEW
  if (completed || (signature && signature.status !== 'pending')) {
    const isSigned = signature.status === 'signed';
    const isDeclined = signature.status === 'declined';
    const isExpired = signature.status === 'expired';
    const completedDate = isSigned ? signature.signed_at : signature.declined_at;

    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title={`Contract ${signature.status} - Blitz`} />
        <Box sx={{ 
          minHeight: '100vh', 
          bgcolor: 'background.default',
          py: 4
        }}>
          <Container maxWidth="lg">
            <Fade in>
              <Box>
                {/* Header */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: 4, 
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <IconButton onClick={handleClose} sx={{ bgcolor: 'action.hover' }}>
                    <BackIcon />
                  </IconButton>
                  <Typography variant="h6" fontWeight={600}>
                    Contract Details
                  </Typography>
                </Paper>

                {/* Status Card */}
                <Zoom in>
                  <Card 
                    elevation={0}
                    sx={{ 
                      mb: 4,
                      background: isSigned 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : isExpired
                          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                      {isSigned && (
                        <>
                          <SignedIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                          <Typography variant="h3" gutterBottom fontWeight={700}>
                            Contract Signed!
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                            Thank you for signing the contract.
                          </Typography>

                          {/* Account Claim CTA */}
                          <Paper 
                            elevation={0}
                            sx={{ 
                              mt: 4, 
                              p: 4, 
                              bgcolor: 'rgba(255,255,255,0.95)',
                              borderRadius: 3,
                              maxWidth: 600,
                              mx: 'auto'
                            }}
                          >
                            <Stack spacing={2} alignItems="center">
                              <Avatar sx={{ bgcolor: 'success.main', width: 64, height: 64 }}>
                                <CreateIcon sx={{ fontSize: 32 }} />
                              </Avatar>
                              <Typography variant="h5" color="text.primary" fontWeight={700}>
                                Create Your Blitz Account
                              </Typography>
                              <Typography variant="body1" color="text.secondary" textAlign="center">
                                Get started with Blitz and manage all your contracts, payments, and collaborations in one place.
                              </Typography>
                              <Button
                                variant="contained"
                                size="large"
                                onClick={handleClaimAccount}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                  mt: 2,
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  px: 4,
                                  py: 1.5,
                                  fontSize: '1.1rem',
                                  '&:hover': {
                                    bgcolor: 'success.dark',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                Claim My Account
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                Your information will be pre-filled for a quick setup
                              </Typography>
                            </Stack>
                          </Paper>
                        </>
                      )}
                      {isDeclined && (
                        <>
                          <ErrorIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                          <Typography variant="h3" gutterBottom fontWeight={700}>
                            Contract Declined
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            You have declined this contract.
                          </Typography>
                          {signature.decline_reason && (
                            <Paper 
                              elevation={0}
                              sx={{ 
                                mt: 3, 
                                p: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: 2,
                                maxWidth: 600,
                                mx: 'auto'
                              }}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                Reason: {signature.decline_reason}
                              </Typography>
                            </Paper>
                          )}
                        </>
                      )}
                      {isExpired && (
                        <>
                          <ScheduleIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                          <Typography variant="h3" gutterBottom fontWeight={700}>
                            Contract Expired
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            This signature request has expired.
                          </Typography>
                        </>
                      )}

                      <Paper 
                        elevation={0}
                        sx={{ 
                          mt: 4, 
                          p: 3, 
                          bgcolor: 'rgba(255,255,255,0.95)',
                          borderRadius: 3,
                          maxWidth: 600,
                          mx: 'auto'
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ArticleIcon color="primary" />
                            <Typography variant="h6" color="text.primary" fontWeight={600}>
                              {template?.name || 'Contract'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon color="action" fontSize="small" />
                            <Typography variant="body1" color="text.secondary">
                              Blitz Systems Corp
                            </Typography>
                          </Box>
                          {completedDate && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ScheduleIcon color="action" fontSize="small" />
                              <Typography variant="body2" color="text.secondary">
                                Completed on {new Date(completedDate).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Paper>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }} justifyContent="center">
                        <Button 
                          variant="contained" 
                          onClick={handleClose} 
                          startIcon={<BackIcon />}
                          size="large"
                          sx={{ 
                            bgcolor: 'white',
                            color: isDeclined ? 'error.main' : isExpired ? 'warning.main' : 'success.main',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                        >
                          Close Window
                        </Button>
                        {(isSigned || isDeclined) && (
                          <Button 
                            variant="outlined" 
                            startIcon={<DownloadIcon />} 
                            onClick={downloadPdf}
                            size="large"
                            sx={{ 
                              borderColor: 'white',
                              color: 'white',
                              '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)'
                              }
                            }}
                          >
                            Download PDF
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Zoom>

                {/* Professional Signed Contract Document */}
                {(isSigned || isDeclined) && (
                  <Box 
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      p: { xs: 2, md: 4 },
                      bgcolor: '#e5e7eb'
                    }}
                  >
                    <Paper 
                      ref={completedDocRef}
                      elevation={3}
                      sx={{ 
                        width: '8.5in',
                        maxWidth: '100%',
                        minHeight: '11in',
                        bgcolor: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        position: 'relative',
                        overflow: 'hidden',
                        '@media print': {
                          boxShadow: 'none',
                          margin: 0,
                          width: '100%',
                        }
                      }}
                    >
                      {/* Watermark */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%) rotate(-45deg)',
                          opacity: 0.03,
                          fontSize: { xs: '80px', md: '140px' },
                          fontWeight: 900,
                          color: isDeclined ? 'error.main' : 'success.main',
                          pointerEvents: 'none',
                          whiteSpace: 'nowrap',
                          zIndex: 0,
                          letterSpacing: '0.1em'
                        }}
                      >
                        {isDeclined ? 'DECLINED' : 'SIGNED'}
                      </Box>

                      {/* Document Header */}
                      <Box sx={{ 
                        p: { xs: 2, md: '1in' },
                        pb: { xs: 2, md: 4 },
                        borderBottom: '4px solid',
                        borderColor: isDeclined ? 'error.main' : 'success.main',
                        background: isDeclined 
                          ? 'linear-gradient(180deg, #FEF2F2 0%, #ffffff 100%)'
                          : 'linear-gradient(180deg, #F0FDF4 0%, #ffffff 100%)',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Box sx={{ 
                            width: { xs: 48, md: 64 }, 
                            height: { xs: 48, md: 64 },
                            borderRadius: 2,
                            bgcolor: isDeclined ? 'error.main' : 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {isDeclined ? (
                              <ErrorIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                            ) : (
                              <VerifiedIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                            )}
                          </Box>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h3" 
                              sx={{ 
                                fontWeight: 800,
                                color: '#0f172a',
                                fontSize: { xs: '24px', md: '36px' },
                                mb: 1,
                                lineHeight: 1.2
                              }}
                            >
                              {template?.name || 'Contract'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Chip 
                                label={isDeclined ? 'DECLINED' : 'DIGITALLY SIGNED'} 
                                size="small"
                                sx={{ 
                                  bgcolor: isDeclined ? 'error.main' : 'success.main',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '11px',
                                  letterSpacing: '0.5px'
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                Blitz Systems Corp
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>

                      {/* Contract Content */}
                      <Box
                        sx={{ 
                          p: { xs: 2, md: '1in' },
                          pt: { xs: 4, md: 6 },
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          lineHeight: 1.8,
                          fontSize: { xs: '14px', md: '16px' },
                          color: '#1e293b',
                          position: 'relative',
                          zIndex: 1,
                          '& h1': { 
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: '20px', md: '24px' },
                            fontWeight: 700,
                            color: '#0f172a',
                            marginTop: { xs: '24px', md: '32px' },
                            marginBottom: { xs: '12px', md: '16px' }
                          },
                          '& h2': { 
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: '18px', md: '20px' },
                            fontWeight: 600,
                            color: '#0f172a',
                            marginTop: { xs: '20px', md: '28px' },
                            marginBottom: { xs: '10px', md: '14px' }
                          },
                          '& h3': { 
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: '16px', md: '18px' },
                            fontWeight: 600,
                            color: '#1e293b',
                            marginTop: { xs: '16px', md: '24px' },
                            marginBottom: { xs: '8px', md: '12px' }
                          },
                          '& p': { 
                            marginBottom: { xs: '14px', md: '18px' },
                            textAlign: 'justify',
                            lineHeight: 1.8
                          },
                          '& strong': { 
                            fontWeight: 600,
                            color: '#0f172a'
                          },
                          '& ol, & ul': {
                            marginTop: { xs: '12px', md: '16px' },
                            marginBottom: { xs: '12px', md: '16px' },
                            paddingLeft: { xs: '20px', md: '28px' }
                          },
                          '& li': {
                            marginBottom: { xs: '8px', md: '12px' }
                          }
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: processed_contract_body || template?.contract_body || '' 
                        }}
                      />

                      {/* Signature Information Section */}
                      <Box sx={{ 
                        p: { xs: 2, md: '1in' },
                        pt: { xs: 4, md: 6 },
                        borderTop: '2px solid #e5e7eb',
                        bgcolor: '#fafbfc',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <Typography 
                          variant="overline" 
                          sx={{ 
                            fontWeight: 700,
                            color: 'text.secondary',
                            letterSpacing: '1px',
                            display: 'block',
                            mb: 3
                          }}
                        >
                          SIGNATURE DETAILS
                        </Typography>

                        <Stack spacing={3}>
                          {/* Signer Information Card */}
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 3,
                              border: '2px solid',
                              borderColor: isDeclined ? 'error.main' : 'success.main',
                              borderRadius: 2,
                              bgcolor: 'white'
                            }}
                          >
                            <Stack spacing={2}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5,
                                pb: 2,
                                borderBottom: '2px solid',
                                borderColor: isDeclined ? 'error.light' : 'success.light'
                              }}>
                                {isDeclined ? (
                                  <ErrorIcon sx={{ color: 'error.main', fontSize: 32 }} />
                                ) : (
                                  <VerifiedIcon sx={{ color: 'success.main', fontSize: 32 }} />
                                )}
                                <Box>
                                  <Typography variant="h6" sx={{ 
                                    fontWeight: 800, 
                                    color: isDeclined ? 'error.main' : 'success.main',
                                    letterSpacing: '0.5px',
                                    fontSize: { xs: '16px', md: '18px' }
                                  }}>
                                    {isDeclined ? 'CONTRACT DECLINED' : 'DIGITALLY SIGNED'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Cryptographically verified and timestamped
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                    SIGNATORY NAME
                                  </Typography>
                                  <Typography variant="body1" fontWeight={700} color="text.primary">
                                    {editedSignerInfo.name || signer_info?.name || 'External Signer'}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                    EMAIL ADDRESS
                                  </Typography>
                                  <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EmailIcon sx={{ fontSize: 16 }} />
                                    {editedSignerInfo.email || signer_info?.email || '—'}
                                  </Typography>
                                </Grid>

                                {(editedSignerInfo.phone || signer_info?.phone) && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                      PHONE NUMBER
                                    </Typography>
                                    <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <PhoneIcon sx={{ fontSize: 16 }} />
                                      {editedSignerInfo.phone || signer_info.phone}
                                    </Typography>
                                  </Grid>
                                )}

                                {completedDate && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                      {isDeclined ? 'DECLINED ON' : 'SIGNED ON'}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary" fontWeight={600}>
                                      {new Date(completedDate).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                      })}
                                    </Typography>
                                  </Grid>
                                )}

                                {/* NEW: Display signature image for complex contracts */}
                                {!is_simple && isSigned && (contractData.signature_image || signatureData) && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                                      SIGNATURE
                                    </Typography>
                                    <Box sx={{ 
                                      border: '2px solid #e5e7eb',
                                      borderRadius: 2,
                                      p: 2,
                                      bgcolor: 'white',
                                      display: 'inline-block'
                                    }}>
                                      <img 
                                        src={contractData.signature_image || signatureData} 
                                        alt="Signature" 
                                        style={{ 
                                          maxWidth: '300px',
                                          height: 'auto',
                                          display: 'block'
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>

                              {isDeclined && signature.decline_reason && (
                                <Box sx={{ 
                                  mt: 2,
                                  p: 2,
                                  bgcolor: '#FEF2F2',
                                  borderRadius: 1,
                                  border: '1px solid #FCA5A5'
                                }}>
                                  <Typography variant="caption" color="error.dark" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                    REASON FOR DECLINING
                                  </Typography>
                                  <Typography variant="body2" color="error.dark">
                                    {signature.decline_reason}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Paper>

                          {/* Technical Verification Details */}
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2,
                              bgcolor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                              VERIFICATION DATA
                            </Typography>
                            <Grid container spacing={1.5}>
                              {signature.ip_address && (
                                <Grid item xs={12} sm={6}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ 
                                      width: 6, 
                                      height: 6, 
                                      borderRadius: '50%', 
                                      bgcolor: 'primary.main' 
                                    }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                        IP Address
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                        {signature.ip_address}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Grid>
                              )}
                              
                              <Grid item xs={12} sm={6}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: 'primary.main' 
                                  }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                      Signature ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                      {signature.id}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Grid>

                              {signature.signature_hash && (
                                <Grid item xs={12}>
                                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                                    <Box sx={{ 
                                      width: 6, 
                                      height: 6, 
                                      borderRadius: '50%', 
                                      bgcolor: 'primary.main',
                                      mt: 0.5
                                    }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                        Cryptographic Hash
                                      </Typography>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontFamily: 'monospace',
                                          fontSize: '11px',
                                          wordBreak: 'break-all',
                                          color: 'text.secondary'
                                        }}
                                      >
                                        {signature.signature_hash}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Grid>
                              )}
                            </Grid>
                          </Paper>

                          {/* Document Footer */}
                          <Box sx={{ 
                            pt: 3,
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                          }}>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Blitz Systems Corp
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Digital Contract Management Platform
                              </Typography>
                            </Stack>
                            
                            <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Document ID: {signatureId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Generated: {new Date().toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>

                      {/* Verification Badge - Bottom Right Corner */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: { xs: 16, md: 32 },
                          right: { xs: 16, md: 32 },
                          zIndex: 2
                        }}
                      >
                        <Paper
                          elevation={4}
                          sx={{
                            p: 1.5,
                            bgcolor: isDeclined ? 'error.main' : 'success.main',
                            color: 'white',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          {isDeclined ? (
                            <ErrorIcon sx={{ fontSize: 24 }} />
                          ) : (
                            <VerifiedIcon sx={{ fontSize: 24 }} />
                          )}
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
                              VERIFIED
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '9px', opacity: 0.9 }}>
                              {new Date(completedDate).getFullYear()}
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Fade>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  // Check if expired
  if (signature?.is_expired) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Contract Expired - Blitz" />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Fade in>
            <Box>
              <Alert severity="warning" sx={{ borderRadius: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Contract Expired
                </Typography>
                <Typography variant="body2">
                  This signature request has expired and can no longer be signed.
                  {signature.expires_at && ` It expired on ${new Date(signature.expires_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}.`}
                </Typography>
              </Alert>
              <Button 
                variant="outlined" 
                onClick={handleClose} 
                startIcon={<BackIcon />}
                size="large"
              >
                Close
              </Button>
            </Box>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  // PENDING VIEW - Enhanced Professional Contract Signing Interface
  return (
    <ThemeProvider theme={modernTheme}>
      <PageTitle title={`${template?.name || 'Contract'} - Blitz`} />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default', 
        py: 4,
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        <Container maxWidth="lg">
          <Fade in>
            <Box>
              {/* Professional Header */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 3,
                  background: 'white',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={2}>
                    <IconButton onClick={handleClose} sx={{ bgcolor: 'action.hover' }}>
                      <BackIcon />
                    </IconButton>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {template?.name || 'Contract'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Blitz Systems Corp • Contract Review & Signature
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={is_simple ? 'Simple Agreement' : 'Formal Contract'}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Paper>

              {/* Signer Identity Card */}
              <Card 
                elevation={0}
                sx={{ 
                  mb: 4,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'primary.light',
                  background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                      <Avatar sx={{ 
                        width: 64, 
                        height: 64, 
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 700
                      }}>
                        {(editedSignerInfo.name || signer_info?.name || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="overline" color="primary" fontWeight={600} sx={{ letterSpacing: '0.1em' }}>
                          SIGNING AS
                        </Typography>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                          {editedSignerInfo.name || signer_info?.name || 'Unknown'}
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" />
                            {editedSignerInfo.email || signer_info?.email || '—'}
                          </Typography>
                          {(editedSignerInfo.phone || signer_info?.phone) && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" />
                              {editedSignerInfo.phone || signer_info?.phone}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEditInfo}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Edit Info
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Expiration Notice */}
              {signature?.expires_at && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3, borderRadius: 3 }}
                  icon={<ScheduleIcon />}
                >
                  <Typography variant="body2" fontWeight={500}>
                    This signature request expires on {new Date(signature.expires_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Alert>
              )}

              {/* Professional Contract Document */}
              <Card 
                elevation={0} 
                sx={{ 
                  mb: 4, 
                  borderRadius: 3,
                  border: '2px solid #e2e8f0',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  bgcolor: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0',
                  p: 3
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ArticleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Contract Document
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please review carefully before signing
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      maxHeight: '600px',
                      overflow: 'auto',
                      p: { xs: 3, md: 5 },
                      bgcolor: 'white',
                      '&::-webkit-scrollbar': {
                        width: '12px',
                      },
                      '&::-webkit-scrollbar-track': {
                        bgcolor: '#f1f5f9',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#cbd5e1',
                        borderRadius: '10px',
                        '&:hover': {
                          bgcolor: '#94a3b8',
                        }
                      },
                    }}
                  >
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: processed_contract_body || template?.contract_body || 'Loading contract content...'
                      }}
                      sx={{ 
                        fontFamily: 'Georgia, "Times New Roman", serif', 
                        lineHeight: 1.8,
                        fontSize: '1.05rem',
                        color: '#1e293b',
                        '& h1, & h2, & h3': {
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 700,
                          color: '#0f172a',
                          marginTop: '2rem',
                          marginBottom: '1rem'
                        },
                        '& p': {
                          marginBottom: '1rem',
                          textAlign: 'justify'
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: '#0f172a'
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Legal Notice */}
              <Alert 
                severity="info" 
                sx={{ mb: 4, borderRadius: 3 }}
                icon={<VerifiedIcon />}
              >
                <Typography variant="body2" fontWeight={500}>
                  {is_simple
                    ? 'By clicking "I Agree", you confirm that you have read and agree to be bound by the terms above. Your agreement will be legally binding.'
                    : 'By drawing your signature and clicking "Sign Contract", you agree to be legally bound by the terms and conditions outlined above. Your digital signature will be recorded with timestamp, IP address, and cryptographic hash for legal verification and authenticity.'}
                </Typography>
              </Alert>

              {/* Professional Action Buttons */}
              <Card elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <CardContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                  <Stack spacing={3}>
                    <Typography variant="h6" fontWeight={600} textAlign="center" color="text.primary">
                      Ready to {is_simple ? 'Agree' : 'Sign'}?
                    </Typography>
                    
                    <Stack 
                      direction={{ xs: 'column-reverse', sm: 'row' }} 
                      spacing={3} 
                      justifyContent="center"
                      alignItems="stretch"
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowDeclineDialog(true)}
                        disabled={actionLoading}
                        size="large"
                        sx={{ 
                          minWidth: { xs: '100%', sm: 180 },
                          py: 1.5,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2
                          }
                        }}
                      >
                        Decline Contract
                      </Button>

                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleAgree}
                        disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <VerifiedIcon />}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 280 },
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 32px rgba(37, 99, 235, 0.5)',
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {actionLoading 
                          ? (is_simple ? 'Processing Agreement...' : 'Processing Signature...') 
                          : (is_simple ? 'I Agree to Terms' : 'Sign Contract Now')}
                      </Button>
                    </Stack>

                    {actionLoading && (
                      <Box>
                        <LinearProgress sx={{ borderRadius: 1 }} />
                        <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 1, display: 'block' }}>
                          Securely processing your {is_simple ? 'agreement' : 'signature'}...
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {error && (
                <Fade in>
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 3 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Edit Signer Info Dialog */}
              <Dialog 
                open={showEditInfoDialog} 
                onClose={() => setShowEditInfoDialog(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EditIcon color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Edit Your Information
                      </Typography>
                    </Box>
                    <IconButton onClick={() => setShowEditInfoDialog(false)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2">
                      Please ensure your information is accurate. This will appear on the signed contract.
                    </Typography>
                  </Alert>
                  
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={editedSignerInfo.name}
                      onChange={(e) => setEditedSignerInfo(prev => ({ ...prev, name: e.target.value }))}
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={editedSignerInfo.email}
                      onChange={(e) => setEditedSignerInfo(prev => ({ ...prev, email: e.target.value }))}
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Phone Number (Optional)"
                      value={editedSignerInfo.phone}
                      onChange={(e) => setEditedSignerInfo(prev => ({ ...prev, phone: e.target.value }))}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                  <Button 
                    onClick={() => setShowEditInfoDialog(false)} 
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveEditedInfo}
                    disabled={!editedSignerInfo.name || !editedSignerInfo.email}
                  >
                    Save Changes
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Enhanced Signature Confirmation Dialog */}
              <Dialog 
                open={showSignConfirmDialog} 
                onClose={() => !actionLoading && setShowSignConfirmDialog(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                  sx: { borderRadius: 4 }
                }}
              >
                <DialogTitle sx={{ pb: 2, pt: 3 }}>
                  <Stack spacing={1}>
                    <Typography variant="h4" fontWeight={700}>
                      {is_simple ? 'Confirm Agreement' : 'Confirm Signature'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please verify your information before proceeding
                    </Typography>
                  </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 4 }}>
                  <Stepper activeStep={signatureStep} sx={{ mb: 4 }}>
                    <Step>
                      <StepLabel>Review</StepLabel>
                    </Step>
                    {!is_simple && (
                      <Step>
                        <StepLabel>Sign</StepLabel>
                      </Step>
                    )}
                    <Step>
                      <StepLabel>Confirm</StepLabel>
                    </Step>
                  </Stepper>

                  {signatureStep === 0 && (
                    <Fade in>
                      <Stack spacing={3}>
                        <Alert severity="info" icon={<ArticleIcon />} sx={{ borderRadius: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            You are about to {is_simple ? 'agree to' : 'digitally sign'}: <strong>{template?.name || 'this contract'}</strong>
                          </Typography>
                        </Alert>

                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            YOUR INFORMATION
                          </Typography>
                          <Stack spacing={1.5} sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <PersonIcon color="action" />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Full Name
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {editedSignerInfo.name}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <EmailIcon color="action" />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Email Address
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {editedSignerInfo.email}
                                </Typography>
                              </Box>
                            </Box>
                            {editedSignerInfo.phone && (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <PhoneIcon color="action" />
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Phone Number
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600}>
                                    {editedSignerInfo.phone}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Paper>

                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          <Typography variant="body2">
                            Is this information correct? If not, please go back and edit your details.
                          </Typography>
                        </Alert>
                      </Stack>
                    </Fade>
                  )}

                  {/* NEW: Signature Step for Complex Contracts */}
                  {signatureStep === 1 && !is_simple && (
                    <Fade in>
                      <Stack spacing={3}>
                        <Alert severity="info" icon={<GestureIcon />} sx={{ borderRadius: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            Please draw your signature in the box below using your mouse, touchpad, or finger on touchscreen devices.
                          </Typography>
                        </Alert>

                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 3, 
                            bgcolor: '#f8fafc', 
                            borderRadius: 2,
                            textAlign: 'center'
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                            DRAW YOUR SIGNATURE
                          </Typography>
                          
                          <Box sx={{ 
                            mt: 2,
                            border: '2px solid',
                            borderColor: signatureEmpty ? 'error.light' : 'success.light',
                            borderRadius: 2,
                            bgcolor: 'white',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            <SignatureCanvas
                              ref={signaturePadRef}
                              canvasProps={{
                                width: 600,
                                height: 200,
                                className: 'signature-canvas',
                                style: {
                                  width: '100%',
                                  height: '200px',
                                  touchAction: 'none'
                                }
                              }}
                              onEnd={handleSignatureEnd}
                              backgroundColor="white"
                              penColor="#1e293b"
                            />
                            {signatureEmpty && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  pointerEvents: 'none',
                                  opacity: 0.3
                                }}
                              >
                                <GestureIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Sign here
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                            <Button
                              variant="outlined"
                              startIcon={<ClearIcon />}
                              onClick={handleClearSignature}
                              size="small"
                              disabled={signatureEmpty}
                            >
                              Clear Signature
                            </Button>
                          </Stack>

                          {signatureEmpty && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                              <Typography variant="body2">
                                Please draw your signature before proceeding
                              </Typography>
                            </Alert>
                          )}
                        </Paper>
                      </Stack>
                    </Fade>
                  )}

                  {/* Final Confirmation Step */}
                  {((signatureStep === 1 && is_simple) || (signatureStep === 2 && !is_simple)) && (
                    <Fade in>
                      <Stack spacing={3}>
                        <Alert severity="success" icon={<VerifiedIcon />} sx={{ borderRadius: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            Your identity has been verified. You're ready to proceed.
                          </Typography>
                        </Alert>

                        <Paper elevation={0} sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                          <Avatar sx={{ 
                            width: 80, 
                            height: 80, 
                            bgcolor: 'success.main',
                            mx: 'auto',
                            mb: 2,
                            fontSize: '2rem'
                          }}>
                            {editedSignerInfo.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="h5" fontWeight={700} gutterBottom>
                            {editedSignerInfo.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {editedSignerInfo.email}
                          </Typography>

                          {/* Show signature preview for complex contracts */}
                          {!is_simple && signatureData && (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                YOUR SIGNATURE
                              </Typography>
                              <Box sx={{ 
                                border: '2px solid #e5e7eb',
                                borderRadius: 2,
                                p: 2,
                                bgcolor: 'white',
                                display: 'inline-block'
                              }}>
                                <img 
                                  src={signatureData} 
                                  alt="Your Signature" 
                                  style={{ 
                                    maxWidth: '300px',
                                    height: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                        </Paper>

                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          By proceeding, you acknowledge that you are {editedSignerInfo.name} and have the authority to {is_simple ? 'agree to' : 'sign'} this contract.
                        </Typography>
                      </Stack>
                    </Fade>
                  )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
                  {signatureStep > 0 && !actionLoading && (
                    <Button 
                      onClick={() => setSignatureStep(prev => prev - 1)}
                      variant="outlined"
                    >
                      Back
                    </Button>
                  )}
                  <Button 
                    onClick={() => !actionLoading && setShowSignConfirmDialog(false)}
                    variant="outlined"
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const maxStep = is_simple ? 1 : 2;
                      if (signatureStep < maxStep) {
                        // Validate signature before moving to final step
                        if (!is_simple && signatureStep === 1 && signatureEmpty) {
                          setError('Please draw your signature before proceeding');
                          return;
                        }
                        setSignatureStep(prev => prev + 1);
                      } else {
                        handleConfirmAndSign();
                      }
                    }}
                    disabled={actionLoading || (!is_simple && signatureStep === 1 && signatureEmpty)}
                    startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : 
                      (signatureStep === (is_simple ? 1 : 2)) ? <VerifiedIcon /> : null}
                    sx={{
                      minWidth: 160,
                      background: (signatureStep === (is_simple ? 1 : 2))
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                      '&:hover': {
                        background: (signatureStep === (is_simple ? 1 : 2))
                          ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                          : 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                      }
                    }}
                  >
                    {actionLoading 
                      ? 'Processing...'
                      : (signatureStep === (is_simple ? 1 : 2))
                        ? (is_simple ? 'Confirm & Agree' : 'Confirm & Sign')
                        : 'Continue'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Decline Dialog */}
              <Dialog 
                open={showDeclineDialog} 
                onClose={() => setShowDeclineDialog(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                      Decline Contract
                    </Typography>
                    <IconButton onClick={() => setShowDeclineDialog(false)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                    Please provide a reason for declining this {is_simple ? 'agreement' : 'contract'}:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter your reason for declining..."
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                  <Button 
                    onClick={() => setShowDeclineDialog(false)} 
                    disabled={actionLoading}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDecline}
                    disabled={!declineReason.trim() || actionLoading}
                    startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {actionLoading ? 'Declining...' : 'Confirm Decline'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ExternalContractAccess;